import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/api";
import { PartnerCard } from "../components/Partner";
import { Link } from "react-router-dom";
import { MARGIN_STATUS } from "../components/MarginBadge";
import { PageHeader } from "../components/Shared";
import { useProfession } from "../lib/professions";
import { toast } from "sonner";
import {
  CurrencyDollar, ChartLine, Package, Users, UserCircle, Receipt, Clock,
} from "@phosphor-icons/react";

function Stat({ label, value, icon: Icon, accent = "indigo" }) {
  const colors = {
    indigo: "text-indigo-600 bg-indigo-50",
    emerald: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    sky: "text-sky-600 bg-sky-50",
    rose: "text-rose-600 bg-rose-50",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="card-crisp p-5 transition hover:-translate-y-0.5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${colors[accent]}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="text-2xl font-heading font-extrabold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const prof = useProfession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">Carregando...</div>;
  if (!data) return null;

  const h = data.hour_cost;
  const c = data.counts;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle={`Visão geral dos custos e indicadores ${prof.de} sua ${prof.place}.`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Hora de Atendimento" value={formatCurrency(h.clinical_hour_cost)} icon={Clock} accent="indigo" />
        <Stat label="Overhead Mensal" value={formatCurrency(h.monthly_overhead)} icon={ChartLine} accent="emerald" />
        <Stat label="Horas/Mês" value={<span>{h.monthly_clinical_hours}h</span>} icon={Clock} accent="sky" />
        <Stat label={prof.procedures} value={c.procedures} icon={prof.icon} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-crisp p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold mb-4">Composição mensal de custos</h3>
          <div className="space-y-4">
            <Bar label="Custos Fixos" value={h.total_fixed} total={h.monthly_overhead} color="bg-indigo-500" />
            <Bar label="Custos Variáveis" value={h.total_variable} total={h.monthly_overhead} color="bg-sky-500" />
            <Bar label="Funcionários (c/ encargos)" value={h.total_employees} total={h.monthly_overhead} color="bg-emerald-500" />
          </div>
          <div className="mt-6 pt-5 border-t border-slate-200 flex items-baseline justify-between">
            <span className="text-sm text-slate-600">Total overhead mensal</span>
            <span className="font-mono-nums text-xl font-bold">{formatCurrency(h.monthly_overhead)}</span>
          </div>
        </div>

        <div className="card-crisp p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Resumo</h3>
          <ul className="space-y-3 text-sm">
            <Row icon={CurrencyDollar} label="Custos fixos" value={c.fixed_costs} />
            <Row icon={ChartLine} label="Custos variáveis" value={c.variable_costs} />
            <Row icon={Package} label="Materiais" value={c.materials} />
            <Row icon={Users} label="Funcionários" value={c.employees} />
            <Row icon={UserCircle} label="Profissionais" value={c.professionals} />
            <Row icon={Receipt} label="Orçamentos" value={c.quotes} />
          </ul>
        </div>
      </div>

      {data.goals && (data.goals.revenue_target > 0 || data.goals.quotes_target > 0) && (
        <div className="mt-8 card-crisp p-6" data-testid="goals-card">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Metas do mês</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {[["Faturamento aprovado", data.goals.revenue_done, data.goals.revenue_target, true], ["Orçamentos aprovados", data.goals.quotes_done, data.goals.quotes_target, false]].filter((g) => g[2] > 0).map(([label, done, target, money]) => {
              const pct = Math.min(100, Math.round((done / target) * 100));
              return (
                <div key={label} data-testid={`goal-${money ? "revenue" : "quotes"}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{label}</span><span className="font-mono-nums font-semibold">{money ? formatCurrency(done) : done} / {money ? formatCurrency(target) : target} · {pct}%</span></div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {data.margin_health && (data.margin_health.saudavel + data.margin_health.apertada + data.margin_health.risco) > 0 && (
        <div className="mt-8 card-crisp p-6" data-testid="margin-health-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Saúde da margem dos seus preços atuais</h3>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold" data-testid="health-ok">{data.margin_health.saudavel} saudáveis</span>
              <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-800 font-semibold" data-testid="health-tight">{data.margin_health.apertada} apertadas</span>
              <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 font-semibold" data-testid="health-risk">{data.margin_health.risco} em risco</span>
            </div>
          </div>
          {data.margin_health.items.length > 0 ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {data.margin_health.items.slice(0, 6).map((it) => (
                <li key={it.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${it.status === "risco" ? "bg-rose-500" : "bg-amber-500"}`} /><span className="text-slate-800">{it.name}</span></div>
                  <div className="text-xs text-slate-600 font-mono-nums">cobra {formatCurrency(it.current_price)} · mín. {formatCurrency(it.price_minimum)} · ideal {formatCurrency(it.price_ideal)}</div>
                </li>
              ))}
            </ul>
          ) : <p className="mt-3 text-sm text-emerald-700">Todos os procedimentos com preço informado estão com margem saudável.</p>}
          <Link to="/app/procedures" className="inline-block mt-3 text-sm text-indigo-600 font-medium hover:underline">Revisar preços →</Link>
        </div>
      )}
      <div className="mt-8"><PartnerCard /></div>
    </div>
  );
}

function Bar({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="font-mono-nums font-medium">{formatCurrency(value)}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <li className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-slate-700">
        <Icon size={16} weight="duotone" className="text-slate-500" />
        {label}
      </span>
      <span className="font-mono-nums font-semibold">{value}</span>
    </li>
  );
}
